/**
 * Focal point support for cropped image tiles.
 *
 * Tiles render with `object-fit: cover`, which defaults to `object-position: 50% 50%`
 * and routinely crops an artist's face out of frame. A media item can carry a focal
 * point — percentages, so the value survives thumbnail resizing and the CDN `width`
 * parameter — that replaces that default.
 *
 * The values live inside the `extendData` JSON column, which also carries unrelated
 * Bunny upload keys. Parse it once per item when the list is mapped; never from a
 * template binding.
 */

/** Focal source precedence: manual beats auto beats fallback beats the 50/50 default. */
export type FocalSource = 'manual' | 'auto' | 'fallback';

export interface FocalPoint {
  focalX: number;
  focalY: number;
  focalSource?: string;
}

interface FocalCapable {
  focalX?: number;
  focalY?: number;
  focalSource?: string;
}

const CENTER_PERCENT = 50;

/** Keys as written by the backend (MediaExtendDataHelper). */
const FOCAL_X_KEY = 'FocalX';
const FOCAL_Y_KEY = 'FocalY';
const FOCAL_SOURCE_KEY = 'FocalSource';

/**
 * Reads the focal point out of a media item's `extendData` JSON.
 * Returns null when absent or unusable — callers treat that as centred.
 */
export function parseFocalPoint(extendData: string): FocalPoint | null {
  if (!extendData) {
    return null;
  }

  let parsed: any;
  try {
    parsed = JSON.parse(extendData);
  } catch {
    // Legacy rows may hold non-JSON text. Centre rather than break the gallery.
    return null;
  }

  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const x = toPercent(parsed[FOCAL_X_KEY]);
  const y = toPercent(parsed[FOCAL_Y_KEY]);

  if (x === null || y === null) {
    return null;
  }

  return {
    focalX: x,
    focalY: y,
    focalSource: parsed[FOCAL_SOURCE_KEY] ?? undefined
  };
}

/**
 * CSS value for `object-position`. Falls back to dead centre so an item without a
 * focal point renders exactly as it did before this feature existed.
 */
export function getFocalPosition(media: FocalCapable | null | undefined): string {
  return `${clampPercent(media?.focalX)}% ${clampPercent(media?.focalY)}%`;
}

/** True when an editor placed this focal point by hand, rather than a detector. */
export function isManualFocalPoint(media: FocalCapable | null | undefined): boolean {
  return media?.focalSource === 'manual';
}

function clampPercent(value: number | null | undefined): number {
  if (typeof value !== 'number' || !isFinite(value)) {
    return CENTER_PERCENT;
  }
  return Math.min(100, Math.max(0, value));
}

/** Accepts numbers and numeric strings; anything else means "not set". */
function toPercent(raw: any): number | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  const value = typeof raw === 'number' ? raw : parseFloat(raw);
  if (typeof value !== 'number' || !isFinite(value)) {
    return null;
  }

  return Math.min(100, Math.max(0, value));
}
